import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { scoreColor } from "@/components/StatusBadge";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { Users, TrendingUp, CheckCircle2, CalendarCheck, Award, Loader2, CalendarDays } from "lucide-react";

const fmtDate = (d) => {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
};

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm animate-fade-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
      </div>
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-6 w-6" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats)
    return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-slate-500">Ringkasan program pengembangan karyawan.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard testid="stat-employees" icon={Users} label="Total Karyawan" value={stats.total_employees} accent="bg-blue-50 text-blue-600" />
        <StatCard testid="stat-passrate" icon={CheckCircle2} label="Tingkat Kelulusan" value={`${stats.pass_rate}%`} sub={`${stats.passed} direkomendasikan`} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="stat-avg" icon={TrendingUp} label="Rata-rata Nilai Akhir" value={stats.avg_score} sub="skala 0–10" accent="bg-orange-50 text-orange-600" />
        <StatCard testid="stat-attendance" icon={CalendarCheck} label="Total Kehadiran" value={stats.total_attendance} sub={`${stats.total_quizzes} kuis dibuat`} accent="bg-amber-50 text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-slate-900">Distribusi Nilai Akhir</h3>
          <p className="text-sm text-slate-500 mb-4">Sebaran jumlah karyawan per rentang nilai.</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.distribution}>
              <XAxis dataKey="range" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {stats.distribution.map((d, i) => (
                  <Cell key={i} fill={["#ef4444", "#F16522", "#25499B", "#10b981"][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-amber-500" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Peringkat Teratas</h3>
          </div>
          <div className="space-y-3">
            {stats.top.map((e, i) => (
              <div key={e.employee_id} data-testid={`top-rank-${i}`} className="flex items-center gap-3">
                <span className="w-6 text-sm font-bold text-slate-400">#{i + 1}</span>
                {e.photo_url ? (
                  <img src={fileUrl(e.photo_url)} alt={e.name} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-500">{e.name[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{e.name}</p>
                  <p className="text-xs text-slate-400 truncate">{e.position}</p>
                </div>
                <span className={`font-heading font-bold ${scoreColor(e.final_score)}`}>{e.final_score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div data-testid="attendance-trend-card" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <CalendarDays className="h-5 w-5 text-[#F16522]" />
          <h3 className="font-heading text-lg font-bold text-slate-900">Tren Kehadiran Harian</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Jumlah karyawan yang hadir setiap hari.</p>
        {stats.attendance_trend && stats.attendance_trend.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.attendance_trend} margin={{ top: 10, right: 20, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F16522" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#F16522" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip
                labelFormatter={fmtDate}
                formatter={(v) => [`${v} karyawan`, "Hadir"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
              />
              <Area type="monotone" dataKey="count" stroke="#F16522" strokeWidth={2.5} fill="url(#attGrad)" dot={{ r: 3, fill: "#F16522" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div data-testid="attendance-trend-empty" className="flex h-[200px] flex-col items-center justify-center text-slate-400">
            <CalendarDays className="h-10 w-10 mb-2 opacity-40" />
            <p className="text-sm">Belum ada data kehadiran.</p>
          </div>
        )}
      </div>
    </div>
  );
}
